import i18next from 'i18next'
import { useTranslation } from 'react-i18next'

import type {
	DebtOverviewDto,
	DebtRecordDto,
	DebtRecordSum,
} from '../types/debtOverviewApi.types'

import {
	DebtRecordRevampedType,
	DebtType,
} from '../types/debtOverviewApi.types'

export function getTotalAmountsSum(debts: DebtOverviewDto): DebtRecordSum {
	let totalAmountSum = 0
	let monthlyAmountSum = 0

	Object.values(debts).forEach((x) => {
		totalAmountSum += x.totalAmount
		monthlyAmountSum += x.monthlyAmount || 0
	})

	return { totalAmountSum, monthlyAmountSum }
}

export function checkAllDebtsInValidState(debts: DebtOverviewDto): boolean {
	for (const x of Object.values(debts)) {
		if (x.monthlyAmount > x.totalAmount) {
			return false
		}
	}
	return true
}

export function checkIfDebtCanBeUpdated(debt: DebtRecordDto): boolean {
	return debt.monthlyAmount != null && debt.monthlyAmount <= debt.totalAmount
}

export function checkCarLeasingDebtCanBeUpdated(debt: DebtRecordDto): boolean {
	return debt.totalAmount === 0 || debt.totalAmount >= debt.monthlyAmount
}

export const getDebtTypeText = (debtType: DebtType): string => {
	switch (debtType) {
		case DebtType.Mortgage:
			return i18next.t('debtOverview-card-mortgage')
		case DebtType.CarLeasing:
			return i18next.t('debtOverview-card-carLeasing')
		case DebtType.StudentLoans:
			return i18next.t('debtOverview-card-studentLoans')
		case DebtType.BankLoans:
			return i18next.t('debtOverview-card-bankLoans')
		case DebtType.ConsumerLoans:
			return i18next.t('debtOverview-card-consumerLoans')
		case DebtType.Overdraft:
			return i18next.t('debtOverview-card-overdraft')
		case DebtType.CreditCardRepayments:
			return i18next.t('debtOverview-card-creditCardRepayments')
		case DebtType.DebtCollection:
			return i18next.t('debtOverview-card-debtCollection')
		case DebtType.PublicDebt:
			return i18next.t('debtOverview-card-publicDebt')
		default:
			return i18next.t('debtOverview-card-other')
	}
}

export const useDebtTypeText = (): ((
	debtType: DebtRecordRevampedType | null,
) => string) => {
	const { t } = useTranslation()

	return (debtType: DebtRecordRevampedType | null): string => {
		switch (debtType) {
			case DebtRecordRevampedType.Unknown:
				return t('debtOverview-card-unknown')
			case DebtRecordRevampedType.ConsumerLoan:
				return t('debtOverview-card-consumerLoans')
			case DebtRecordRevampedType.StudentLoan:
				return t('debtOverview-card-studentLoans')
			case DebtRecordRevampedType.MortgageByCreditInstitution:
				return t('debtOverview-card-mortgage')
			case DebtRecordRevampedType.MortgageByBank:
				return t('debtOverview-card-mortgage-by-bank')
			case DebtRecordRevampedType.CarLoan:
				return t('debtOverview-card-car-loan')
			case DebtRecordRevampedType.Overdraft:
				return t('debtOverview-card-overdraft')
			case DebtRecordRevampedType.OtherLoan:
				return t('debtOverview-card-other')
			case DebtRecordRevampedType.BankLoan:
				return t('debtOverview-card-bankLoans')
			case DebtRecordRevampedType.HomeLoan:
				return t('debtOverview-card-homeLoan')
			case DebtRecordRevampedType.DebtCollection:
				return t('debtOverview-card-debtCollection')
			case DebtRecordRevampedType.CreditCard:
				return t('debtOverview-card-creditCardRepayments')
			case DebtRecordRevampedType.PublicDebt:
				return t('debtOverview-card-publicDebt')
			case DebtRecordRevampedType.ForeignDebt:
				return t('debtOverview-card-foreign-debt')
			default:
				return t('debtOverview-card-other')
		}
	}
}

export const useDebtAtCreditorText = (): ((
	debtType: DebtRecordRevampedType | null,
	creditorName: string,
) => string) => {
	const { t } = useTranslation()
	return (debtType: DebtRecordRevampedType | null, creditorName: string) => {
		switch (debtType) {
			case DebtRecordRevampedType.Unknown:
				return t('debtOverview-debt-at-unknown', { creditorName })
			case DebtRecordRevampedType.ConsumerLoan:
				return t('debtOverview-debt-at-consumerLoans', { creditorName })
			case DebtRecordRevampedType.StudentLoan:
				return t('debtOverview-debt-at-studentLoans', { creditorName })
			case DebtRecordRevampedType.MortgageByCreditInstitution:
				return t('debtOverview-debt-at-mortgage', { creditorName })
			case DebtRecordRevampedType.MortgageByBank:
				return t('debtOverview-debt-at-mortgage-by-bank', { creditorName })
			case DebtRecordRevampedType.CarLoan:
				return t('debtOverview-debt-at-car-loan', { creditorName })
			case DebtRecordRevampedType.Overdraft:
				return t('debtOverview-debt-at-overdraft', { creditorName })
			case DebtRecordRevampedType.OtherLoan:
				return t('debtOverview-debt-at-other', { creditorName })
			case DebtRecordRevampedType.BankLoan:
				return t('debtOverview-debt-at-bankLoans', { creditorName })
			case DebtRecordRevampedType.HomeLoan:
				return t('debtOverview-debt-at-homeLoan', { creditorName })
			case DebtRecordRevampedType.DebtCollection:
				return t('debtOverview-debt-at-debtCollection', { creditorName })
			case DebtRecordRevampedType.CreditCard:
				return t('debtOverview-debt-at-creditCard', { creditorName })
			case DebtRecordRevampedType.PublicDebt:
				return t('debtOverview-debt-at-publicDebt', { creditorName })
			case DebtRecordRevampedType.ForeignDebt:
				return t('debtOverview-debt-at-foreign-debt', { creditorName })
			default:
				return t('debtOverview-debt-at-other', { creditorName })
		}
	}
}
